/**
 * Turns the map the backend returns into something a force graph and a set of
 * lists can both read: stable ids, resolved edge endpoints, per-node
 * neighbours, and the counts the legend needs.
 */

import type { IssueMap, MapEdge, MapNode, SubjectRef } from '../../../types/issues';
import { camp, influenceOf, isUnknown, subjectHref, subjectKey } from './position';
import { clampPosition } from '../components/format';

export { subjectKey };

export interface MapGraphNode {
  id: string;
  node: MapNode;
  label: string;
  /** 0–1, normalised from the issue influence (or the directory figure) */
  influence: number;
  /** Canvas radius in graph units */
  radius: number;
  bloc: number | null;
}

export interface MapGraphLink {
  id: string;
  source: string;
  target: string;
  edge: MapEdge;
  crossesCamps: boolean;
}

export interface BlocInfo {
  id: number;
  label: string;
  size: number;
  meanPosition: number;
  influence: number;
  members: SubjectRef[];
  /** Member ids that are actually on the map */
  memberIds: string[];
}

export interface Neighbour {
  id: string;
  node: MapGraphNode;
  edge: MapEdge;
  crossesCamps: boolean;
}

export interface MapModel {
  nodes: MapGraphNode[];
  links: MapGraphLink[];
  byId: Map<string, MapGraphNode>;
  blocs: BlocInfo[];
  neighbours: Map<string, Neighbour[]>;
  /** How many actors sit at each of the five steps, plus the unread */
  counts: Record<number | 'unknown', number>;
  bridges: MapGraphNode[];
  swing: MapGraphNode[];
  /** Ties whose two ends sit in opposite camps — the channels a message travels */
  crossings: MapGraphLink[];
  /** Every distinct category on the board, for scenario segment levers */
  categories: string[];
}

const RADIUS_MIN = 4.5;
const RADIUS_MAX = 15;

export const radiusOf = (influence: number): number => RADIUS_MIN + (RADIUS_MAX - RADIUS_MIN) * Math.sqrt(Math.max(0, Math.min(1, influence)));

export const EMPTY_MODEL: MapModel = {
  nodes: [],
  links: [],
  byId: new Map(),
  blocs: [],
  neighbours: new Map(),
  counts: { 2: 0, 1: 0, 0: 0, [-1]: 0, [-2]: 0, unknown: 0 },
  bridges: [],
  swing: [],
  crossings: [],
  categories: [],
};

/**
 * Edge endpoints may arrive as `stakeholder:42` or as a bare id — resolve both
 * rather than silently dropping half the network.
 */
function endpointIndex(nodes: MapGraphNode[]): Map<string, string> {
  const index = new Map<string, string>();
  const ambiguous = new Set<string>();
  nodes.forEach((n) => {
    index.set(n.id, n.id);
    const bare = n.node.subject.id;
    if (index.has(bare) && index.get(bare) !== n.id) ambiguous.add(bare);
    else index.set(bare, n.id);
  });
  ambiguous.forEach((key) => index.delete(key));
  return index;
}

export function buildMapModel(map: IssueMap | null | undefined): MapModel {
  if (!map || !Array.isArray(map.nodes) || map.nodes.length === 0) return EMPTY_MODEL;

  const nodes: MapGraphNode[] = map.nodes.map((node) => {
    const influence = influenceOf(node.issueInfluence, node.directoryInfluence);
    return {
      id: subjectKey(node.subject),
      node,
      label: node.subject.label || 'Unknown',
      influence,
      radius: radiusOf(influence),
      bloc: node.bloc ?? null,
    };
  });

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const resolve = endpointIndex(nodes);

  const seen = new Set<string>();
  const links: MapGraphLink[] = [];
  (map.edges || []).forEach((edge, i) => {
    const source = resolve.get(edge.source);
    const target = resolve.get(edge.target);
    if (!source || !target || source === target) return;
    const key = source < target ? `${source}|${target}|${edge.kind}` : `${target}|${source}|${edge.kind}`;
    if (seen.has(key)) return;
    seen.add(key);
    const a = byId.get(source)!;
    const b = byId.get(target)!;
    const campA = camp(a.node.position);
    const campB = camp(b.node.position);
    links.push({
      id: `e${i}:${key}`,
      source,
      target,
      edge,
      // Trust the backend's own reading, but fall back to the positions we hold
      crossesCamps:
        edge.crossesCamps ??
        (campA !== 'undecided' && campB !== 'undecided' && campA !== campB),
    });
  });

  const neighbours = new Map<string, Neighbour[]>();
  const push = (from: string, to: string, link: MapGraphLink) => {
    const other = byId.get(to);
    if (!other) return;
    const list = neighbours.get(from) || [];
    list.push({ id: to, node: other, edge: link.edge, crossesCamps: link.crossesCamps });
    neighbours.set(from, list);
  };
  links.forEach((link) => {
    push(link.source, link.target, link);
    push(link.target, link.source, link);
  });
  neighbours.forEach((list) => list.sort((a, b) => b.node.influence - a.node.influence));

  const counts: Record<number | 'unknown', number> = { 2: 0, 1: 0, 0: 0, [-1]: 0, [-2]: 0, unknown: 0 };
  nodes.forEach((n) => {
    if (isUnknown(n.node.position)) counts.unknown += 1;
    else counts[clampPosition(n.node.position as number)] += 1;
  });

  const blocs: BlocInfo[] = (map.blocs || []).map((bloc) => ({
    ...bloc,
    members: bloc.members || [],
    memberIds: (bloc.members || []).map(subjectKey).filter((id) => byId.has(id)),
  }));

  const categories = Array.from(
    new Set(nodes.map((n) => n.node.category).filter((c): c is string => !!c && c.trim().length > 0)),
  ).sort((a, b) => a.localeCompare(b));

  return {
    nodes,
    links,
    byId,
    blocs,
    neighbours,
    counts,
    bridges: nodes.filter((n) => n.node.isBridge).sort((a, b) => b.influence - a.influence),
    swing: nodes.filter((n) => n.node.isSwing).sort((a, b) => b.influence - a.influence),
    crossings: links.filter((l) => l.crossesCamps),
    categories,
  };
}

/** `/stakeholders/42` — where the full dossier lives */
export function profilePath(subject: SubjectRef): string {
  return subjectHref(subject);
}

export const PRIORITY_LABEL: Record<string, string> = {
  swing: 'Swing',
  bridge: 'Bridge',
  blocker: 'Blocker',
  amplifier: 'Amplifier',
  unknown: 'Unread',
};

export const PRIORITY_HINT: Record<string, string> = {
  swing: 'Undecided, and heavy enough to matter either way',
  bridge: 'Talks to both camps — a message can travel through them',
  blocker: 'Against us, with the weight to stop this',
  amplifier: 'With us already, and able to carry others',
  unknown: 'Influential, with nothing on record — a blind spot',
};

/**
 * Categories arrive as taxonomy keys ('community-authority'); nobody should
 * have to read a slug. The key itself is what gets sent back to the server.
 */
export function categoryLabel(key: string | null | undefined): string {
  const words = (key || '').replace(/[-_]+/g, ' ').trim();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Blocs the backend did not name still need something to call them */
export function blocLabel(bloc: BlocInfo | undefined | null): string {
  if (!bloc) return 'Unaligned';
  return bloc.label?.trim() || `Bloc ${bloc.id + 1}`;
}
